import React, { useCallback, useMemo, useRef } from 'react';
import { SEC_FILTER_DEFAULTS } from '../utils/secFilingFilters';
import SecFilingInsightsCompanyCard from './SecFilingInsightsCompanyCard';
import SecFilingInsightsPeriodTable from './SecFilingInsightsPeriodTable';

export default function SecFilingInsightsSummary({
  period,
  summary,
  periodTableRows = [],
  loading = false,
  animateEnter = false,
  periodSwitchVersion = 0,
  filters,
  tableCompanyFilter,
  onTableCompanyFilterChange,
  onViewFilingsCompany,
  tablePage,
  onTablePageChange,
  subjectCompany = '',
}) {
  const companies = summary?.companies || [];
  const rangeLabel = summary?.rangeLabel || '';
  const periodTableRef = useRef(null);

  const tableCompanyFilterLabel = useMemo(() => {
    if (!tableCompanyFilter) return null;
    const card = companies.find(
      (item) => item.companyName === tableCompanyFilter,
    );
    return card?.displayName || tableCompanyFilter;
  }, [tableCompanyFilter, companies]);

  const handleViewFilings = useCallback((companyName) => {
    onTableCompanyFilterChange?.(companyName);
    onViewFilingsCompany?.(companyName);
    onTablePageChange?.(1);
    requestAnimationFrame(() => {
      periodTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }, [onTableCompanyFilterChange, onViewFilingsCompany, onTablePageChange]);

  const handleClearCompanyFilter = useCallback(() => {
    onTableCompanyFilterChange?.(null);
    if (tableCompanyFilter) {
      onViewFilingsCompany?.(SEC_FILTER_DEFAULTS.company);
    }
  }, [tableCompanyFilter, onTableCompanyFilterChange, onViewFilingsCompany]);

  return (
    <section className="sec-filing-insights-summary" aria-label="Top competitors summary">
      <div className="sec-filing-insights-summary__header">
        <h2 className="sec-filing-insights-summary__title">Top Competitor&apos;s Summary</h2>
      </div>
      {loading && !companies.length ? (
        <p className="sec-filing-insights-summary__meta">Loading insights…</p>
      ) : null}
      <div className="sec-filing-insights-summary__switch">
        <div className="sec-filing-insights-summary__grid">
          {companies.map((card, index) => (
            <SecFilingInsightsCompanyCard
              key={card.companyName}
              card={card}
              chartIndex={index}
              animateEnter={animateEnter}
              periodSwitchVersion={periodSwitchVersion}
              isTableFilterActive={tableCompanyFilter === card.companyName}
              onViewFilings={handleViewFilings}
            />
          ))}
        </div>

        <div ref={periodTableRef}>
          <SecFilingInsightsPeriodTable
            period={period}
            rangeLabel={rangeLabel}
            rows={periodTableRows}
            periodSwitchVersion={periodSwitchVersion}
            filters={filters}
            companyFilter={tableCompanyFilter}
            companyFilterLabel={tableCompanyFilterLabel}
            onClearCompanyFilter={handleClearCompanyFilter}
            currentPage={tablePage}
            onPageChange={onTablePageChange}
            subjectCompany={subjectCompany}
          />
        </div>
      </div>
    </section>
  );
}
